import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import type {
  AdminLoginRequest,
  AdminLoginResponse,
  AuthSession,
  CreateAdminRequest,
  PlatformAdmin,
} from '../models/auth.model';
import { TokenStorage } from './token.storage';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorage);

  private readonly sessionSignal = signal<AuthSession | null>(this.restoreSession());

  readonly session = this.sessionSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.sessionSignal()?.token);
  readonly currentAdmin = computed(() => this.sessionSignal()?.admin ?? null);

  login(credentials: AdminLoginRequest): Observable<AuthSession> {
    return this.http
      .post<AdminLoginResponse>(`${environment.apiUrl}/auth/admin/login`, credentials)
      .pipe(
        map((res) => ({ token: res.accessToken, admin: res.admin })),
        tap((session) => this.persistSession(session)),
        catchError((err) => throwError(() => err)),
      );
  }

  logout(): void {
    this.tokenStorage.clearAll();
    this.sessionSignal.set(null);
    void this.router.navigate(['/login']);
  }

  listAdmins(): Observable<PlatformAdmin[]> {
    return this.http.get<PlatformAdmin[]>(`${environment.apiUrl}/auth/admin/admins`);
  }

  createAdmin(dto: CreateAdminRequest): Observable<PlatformAdmin> {
    return this.http.post<PlatformAdmin>(`${environment.apiUrl}/auth/admin/admins`, dto);
  }

  private persistSession(session: AuthSession): void {
    this.tokenStorage.setToken(session.token);
    this.tokenStorage.setAdminProfile(session.admin);
    this.sessionSignal.set(session);
  }

  private restoreSession(): AuthSession | null {
    const token = this.tokenStorage.getToken();
    const admin = this.tokenStorage.getAdminProfile<AuthSession['admin']>();
    if (!token || !admin) return null;
    return { token, admin };
  }
}
